import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import dayjs from 'dayjs'
import specializedReportService, { specializedAttachFormats } from '@api/v1/specializedReportService'
import reportJobService from '@api/v1/reportJobService'
import useLatestRun from '@hooks/useLatestRun'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import { monthlyProductivitySavedCriteria } from '@components/reports/savedRunCriteria'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { objectField, numberField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters } from '@components/reports/reportFilterSummary'
import CsvReportGrid from '@components/reports/CsvReportGrid'

const TYPE_USERS = 1
const TYPE_COMPANIES = 3

// CC-50020: MonthlyProductivityFilter's fields are companyId/companyName — the
// old clientId/clientName keys bound to nothing (Newtonsoft ignores unknown
// properties), so the Client filter silently never scoped the proc. The specs
// below are the ONLY place those key names appear now, and
// MonthlyProductivityReport.wire.test.jsx pins them.
const FIELDS = [
  objectField({ key: 'client', label: 'Client', typeId: TYPE_COMPANIES, idKey: 'companyId', nameKey: 'companyName' }),
  objectField({ key: 'timekeeper', label: 'Timekeeper', typeId: TYPE_USERS, idKey: 'userId', nameKey: 'userName' }),
  numberField({ key: 'year', label: 'Year', valueKey: 'year', fallback: dayjs().year() }),
]

const makeDefaults = () => ({
  companyId: null, companyName: '',
  userId: null, userName: '',
  year: dayjs().year(),
})

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err?.response?.data?.error || err.message || 'Report failed'

const MonthlyProductivityReport = ({ reportId, name = 'Monthly Productivity Recap', summary, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria (the split button's mid-run "Run report now" is also
  // disabled now; this gate is the structural guarantee).
  const {
    running, error, ran, runKey, run: startRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(filters.year ? `Year ${filters.year}` : null), [filters])
  const [data, setData] = useState({ headers: [], rows: [], meta: [] })
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Spec 185 (CC-53435): the reopened job's criteria line, built from ITS stored params
  // at the reopen's commit (null = the shell's "not recorded"). Never seeded into
  // `filters`: the filter bar is not the saved run's, and a reopen must not start a run.
  const [savedCriteria, setSavedCriteria] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)

  const runReport = useCallback(() => startRun(async () => {
    const jid = jobRef.current
    jobRef.current = null // consumed — subsequent runs are fresh
    if (jid) {
      // Tray "View report" → reopen the exact saved snapshot + show its run time.
      const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
      return () => { setSavedAsOf(st?.createdAt || null); setSavedCriteria(monthlyProductivitySavedCriteria(st?.savedParams)); setData(r?.data || { headers: [], rows: [], meta: [] }) }
    }
    const result = await specializedReportService.monthlyProductivity.run(filters, { reportId })
    // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
    return () => { setSavedAsOf(null); setData(result || { headers: [], rows: [], meta: [] }) }
  }, { criteria: { filtersSummary } }), [filters, reportId, startRun, filtersSummary])

  // Spec 131: deferred and cancellable, so StrictMode's simulated remount starts
  // ONE mount run, not two.
  useEffect(() => {
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // CC-50013 G2 / CC-50148: the shared metadata-driven bar — same filter widget
  // registry as the 119 generic reports, plus Reset and the split Run/Queue
  // control. The wire object is unchanged; see FIELDS above.
  const filterBar = (
    <SpecializedFilterBar
      fields={FIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      queue={{
        reportId,
        name,
        // Must match specializedReportService's own job actionKey (the report
        // path) or a queued run would get a different dedupe key than an
        // interactive one and never reuse it.
        actionKey: 'monthly-productivity',
        endpoint: (f) => ({ method: 'POST', path: '/reports/monthly-productivity', body: f }),
        // Spec 200: an emailed copy carries this report's own export, asked what the run asks.
        attachFormats: specializedAttachFormats('/reports/monthly-productivity'),
      }}
      actions={(
        <ReportViewsMenu
          storageKey="report:monthly-productivity"
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} summaryData={data} savedAsOf={savedAsOf}
      savedRunFiltersSummary={savedAsOf != null ? savedCriteria : undefined}
      download={(format, getToken) =>
        specializedReportService.monthlyProductivity.download(filters, format, getToken, `monthly-productivity.${format}`)
      }
    >
      <CsvReportGrid key={view.gridKey} data={data} storageKey="report:monthly-productivity" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
    </SpecializedReportShell>
  )
}

export default MonthlyProductivityReport
